/**
 * Computes page-cut Y-positions (in canvas pixels) using a greedy
 * block-boundary algorithm.
 *
 * Each page takes the largest block-bottom that fits within
 * pageTop + usablePageHeight. A block whose bottom exceeds the current page
 * limit is left for the next page. If even the first block on a fresh page
 * exceeds the limit (oversized block), a raw pixel cut is made at
 * Math.min(pageTop + usablePageHeight, total) WITHOUT advancing the block
 * pointer, so the same block is re-evaluated on the next page.
 *
 * @param {number[]} blockBottoms - Sorted ascending array of block-bottom
 *   offsets in canvas pixels. The last value equals the total content height.
 * @param {number} usablePageHeight - Usable canvas-pixel height per page
 *   (e.g. Math.round(277 / (190 / 1588)) ≈ 2315 for A4 at scale:2).
 * @returns {number[]} Array of page-cut Y-positions in canvas pixels.
 *   Each entry is the canvas Y at which one PDF page ends and the next begins.
 */
export function computePageCuts(blockBottoms, usablePageHeight) {
  if (!blockBottoms.length || usablePageHeight <= 0) return [];

  const cuts = [];
  const total = blockBottoms[blockBottoms.length - 1];
  let pageTop = 0;
  let i = 0;

  while (pageTop < total) {
    const limit = pageTop + usablePageHeight;
    let fit = -1;

    // Collect all block-bottoms that fit within this page's limit
    while (i < blockBottoms.length && blockBottoms[i] <= limit) {
      fit = blockBottoms[i];
      i++;
    }

    if (fit === -1) {
      // Oversized block: no block fits on this page → raw pixel cut
      // Do NOT advance i so the same block is re-evaluated next page
      const cut = Math.min(limit, total);
      cuts.push(cut);
      pageTop = cut;
    } else {
      // Greedy: cut at the largest block-bottom that fits
      cuts.push(fit);
      pageTop = fit;
    }
  }

  return cuts;
}
